import { authService } from 'modules/auth/services/authService';

export type NotificacaoCanal = 'EMAIL' | 'IN_APP' | 'SMS' | 'PUSH' | 'WEBHOOK' | 'WHATSAPP';

export interface TipoEventoDto {
  tipo: string;
  descricao: string;
  moduloOrigem: string;
  canaisDefault: string[];
  payloadExemploJson: string;
}

export interface TemplateListaDto {
  id: string;
  tipoEvento: string;
  canal: NotificacaoCanal;
  locale: string;
  nome: string;
  assuntoTemplate: string;
  ativo: boolean;
  criadoEm: string;
  atualizadoEm: string;
}

export interface TemplateDetalheDto {
  id: string;
  tipoEvento: string;
  canal: NotificacaoCanal;
  locale: string;
  nome: string;
  assuntoTemplate: string;
  corpoTextoTemplate: string;
  corpoHtmlTemplate: string | null;
  ativo: boolean;
  criadoEm: string;
  atualizadoEm: string;
}

export interface CriarTemplateRequest {
  tipoEvento: string;
  canal: NotificacaoCanal;
  locale?: string | null;
  nome: string;
  assunto: string;
  corpoTexto: string;
  corpoHtml?: string | null;
}

export interface AtualizarTemplateRequest {
  nome: string;
  assunto: string;
  corpoTexto: string;
  corpoHtml: string | null;
  ativo: boolean;
}

export interface PreviewTemplateRequest {
  assuntoTemplate?: string | null;
  corpoTextoTemplate?: string | null;
  corpoHtmlTemplate?: string | null;
  payloadJson?: string | null;
  destinatarioNome?: string | null;
}

export interface PreviewTemplateDto {
  assunto: string;
  corpoTexto: string;
  corpoHtml: string | null;
  sucesso: boolean;
  erro: string | null;
}

export interface EnvioListaDto {
  id: string;
  eventoNotificacaoId: string;
  eventoTipo: string;
  canal: NotificacaoCanal;
  destinatarioEndereco: string;
  destinatarioNome: string | null;
  assunto: string;
  statusAtual: string;
  tentativas: number;
  maxTentativas: number;
  ultimoErro: string | null;
  criadaEm: string;
  enviandoEm: string | null;
  enfileiradaEm: string | null;
  entregueEm: string | null;
  primeiraAberturaEm: string | null;
  aberturas: number;
  primeiroCliqueEm: string | null;
  cliques: number;
  falhouEm: string | null;
}

export interface EventoProviderDto {
  id: string;
  providerNome: string;
  providerMessageId: string | null;
  tipoEvento: string;
  timestampEvento: string;
  email: string | null;
  ip: string | null;
  userAgent: string | null;
  urlClicada: string | null;
  motivo: string | null;
  criadoEm: string;
}

export interface EnvioDetalheDto {
  id: string;
  eventoNotificacaoId: string;
  eventoTipo: string;
  eventoModuloOrigem: string;
  canal: NotificacaoCanal;
  destinatarioEndereco: string;
  destinatarioNome: string | null;
  destinatarioUsuarioId: string | null;
  destinatarioPessoaId: string | null;
  templateUtilizado: string | null;
  assunto: string;
  corpoTexto: string;
  corpoHtml: string | null;
  statusAtual: string;
  ultimoErro: string | null;
  tentativas: number;
  maxTentativas: number;
  proximaTentativaEm: string | null;
  providerNome: string | null;
  providerMessageId: string | null;
  criadaEm: string;
  enviandoEm: string | null;
  enfileiradaEm: string | null;
  entregueEm: string | null;
  primeiraAberturaEm: string | null;
  aberturas: number;
  primeiroCliqueEm: string | null;
  cliques: number;
  falhouEm: string | null;
  eventosProvider: EventoProviderDto[];
}

export interface EnvioDashboardDto {
  totalNotificacoes: number;
  totalEventos: number;
  porStatus: Record<string, number>;
  porCanal: Record<string, number>;
  porTipoEvento: Record<string, number>;
}

export interface EnvioPaginaDto {
  itens: EnvioListaDto[];
  total: number;
  pagina: number;
  tamanhoPagina: number;
}

export interface ListarEnviosParams {
  canal?: NotificacaoCanal | '';
  status?: string;
  tipoEvento?: string;
  desde?: string;
  ate?: string;
  busca?: string;
  pagina?: number;
  tamanhoPagina?: number;
}

type Primitive = string | number | boolean;
const qs = (params: Record<string, Primitive | null | undefined> | object) => {
  const entries = Object.entries(params as Record<string, unknown>)
    .filter(([, v]) => v !== undefined && v !== null && v !== '');
  if (entries.length === 0) return '';
  return '?' + entries.map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`).join('&');
};

export interface AlertaItemDto {
  codigo: string;
  nome: string;
  moduloOrigem: string;
  descricao: string;
  suportaHorario: boolean;
  suportaDias: boolean;
  suportaDestinatariosExtras: boolean;
  ativo: boolean;
  horaBrt: number;
  dias: number[];
  destinatariosExtras: string[];
  alteradoEm: string | null;
  /** Alerta disparado por evento (sem job) não tem "testar agora". */
  podeDispararAgora: boolean;
}

export interface SalvarAlertaRequest {
  ativo: boolean;
  horaBrt: number;
  dias: number[];
  destinatariosExtras: string[];
}

export interface MinhaNotificacaoDto {
  id: string;
  assunto: string;
  corpoTexto: string;
  linkUrl: string | null;
  lidaEm: string | null;
  arquivadaEm: string | null;
  criadaEm: string;
  tipoEvento: string;
  moduloOrigem: string;
  severidade: string;
}

export interface MinhasNotificacoesPaginaDto {
  itens: MinhaNotificacaoDto[];
  total: number;
  pagina: number;
  tamanhoPagina: number;
}

export interface ContagemPorChaveDto {
  chave: string;
  total: number;
}

export interface MinhasNotificacoesResumoDto {
  caixa: number;
  naoLidas: number;
  lidas: number;
  arquivadas: number;
  porModulo: ContagemPorChaveDto[];
  porTipo: ContagemPorChaveDto[];
}

/**
 * Converte o linkUrl da notificação numa rota do SPA.
 *
 * O mesmo campo `link` do evento alimenta o sino e (em alguns módulos) o e-mail, que precisa
 * de URL absoluta — e o navigate() do React Router trata URL absoluta como caminho relativo,
 * concatenando na rota atual e caindo em 404. Aqui a URL absoluta vira o caminho dela.
 * O deep-link sempre aponta para esta própria aplicação, então o host é descartável — e ele
 * pode até estar errado, já que vem de configuração (App:BaseUrl) que varia por ambiente.
 */
export function rotaDaNotificacao(linkUrl: string | null | undefined): string | null {
  const valor = (linkUrl ?? '').trim();
  if (!valor) return null;
  if (valor.startsWith('/')) return valor;
  try {
    const url = new URL(valor);
    return url.pathname + url.search + url.hash;
  } catch {
    return null; // não é URL nem caminho: melhor não navegar do que navegar errado
  }
}

/** Situação no filtro da caixa. Vazio = todas. */
export type SituacaoNotificacao = '' | 'nao_lidas' | 'lidas';

export interface ListarMinhasParams {
  situacao?: SituacaoNotificacao;
  arquivadas?: boolean;
  modulo?: string;
  tipo?: string;
  busca?: string;
  pagina?: number;
  tamanhoPagina?: number;
}

export const notificacoesService = {
  listarTiposEvento(): Promise<TipoEventoDto[] | null> {
    return authService.authFetch<TipoEventoDto[]>('/api/notificacoes/tipos-evento');
  },

  // Caixa in-app do usuário atual (sino + página "Minhas notificações")
  minhas: {
    listar(params: ListarMinhasParams = {}): Promise<MinhasNotificacoesPaginaDto | null> {
      return authService.authFetch<MinhasNotificacoesPaginaDto>(`/api/notificacoes/minhas${qs(params)}`);
    },
    resumo(): Promise<MinhasNotificacoesResumoDto | null> {
      return authService.authFetch<MinhasNotificacoesResumoDto>('/api/notificacoes/minhas/resumo');
    },
    naoLidas(): Promise<number | null> {
      return authService.authFetch<number>('/api/notificacoes/unread-count');
    },
    async marcarLida(id: string): Promise<void> {
      await authService.authFetch<null>(`/api/notificacoes/${id}/marcar-lida`, { method: 'POST' });
    },
    async marcarNaoLida(id: string): Promise<void> {
      await authService.authFetch<null>(`/api/notificacoes/${id}/marcar-nao-lida`, { method: 'POST' });
    },
    async arquivar(id: string): Promise<void> {
      await authService.authFetch<null>(`/api/notificacoes/${id}/arquivar`, { method: 'POST' });
    },
    async desarquivar(id: string): Promise<void> {
      await authService.authFetch<null>(`/api/notificacoes/${id}/desarquivar`, { method: 'POST' });
    },
    async marcarTodasLidas(): Promise<void> {
      await authService.authFetch<null>('/api/notificacoes/marcar-todas-lidas', { method: 'POST' });
    },
    async arquivarLidas(): Promise<void> {
      await authService.authFetch<null>('/api/notificacoes/arquivar-lidas', { method: 'POST' });
    }
  },

  templates: {
    listar(params: { tipoEvento?: string; canal?: NotificacaoCanal | '' } = {}): Promise<TemplateListaDto[] | null> {
      return authService.authFetch<TemplateListaDto[]>(`/api/notificacoes/templates${qs(params)}`);
    },
    obter(id: string): Promise<TemplateDetalheDto | null> {
      return authService.authFetch<TemplateDetalheDto>(`/api/notificacoes/templates/${id}`);
    },
    criar(payload: CriarTemplateRequest): Promise<TemplateDetalheDto | null> {
      return authService.authFetch<TemplateDetalheDto>('/api/notificacoes/templates', {
        method: 'POST',
        body: JSON.stringify(payload)
      });
    },
    atualizar(id: string, payload: AtualizarTemplateRequest): Promise<TemplateDetalheDto | null> {
      return authService.authFetch<TemplateDetalheDto>(`/api/notificacoes/templates/${id}`, {
        method: 'PUT',
        body: JSON.stringify(payload)
      });
    },
    preview(payload: PreviewTemplateRequest): Promise<PreviewTemplateDto | null> {
      return authService.authFetch<PreviewTemplateDto>('/api/notificacoes/templates/preview', {
        method: 'POST',
        body: JSON.stringify(payload)
      });
    }
  },

  envios: {
    listar(params: ListarEnviosParams = {}): Promise<EnvioPaginaDto | null> {
      return authService.authFetch<EnvioPaginaDto>(`/api/notificacoes/envios${qs(params)}`);
    },
    obter(id: string): Promise<EnvioDetalheDto | null> {
      return authService.authFetch<EnvioDetalheDto>(`/api/notificacoes/envios/${id}`);
    },
    dashboard(params: { desde?: string; ate?: string } = {}): Promise<EnvioDashboardDto | null> {
      return authService.authFetch<EnvioDashboardDto>(`/api/notificacoes/dashboard${qs(params)}`);
    }
  },

  alertas: {
    listar(): Promise<AlertaItemDto[] | null> {
      return authService.authFetch<AlertaItemDto[]>('/api/notificacoes/alertas');
    },
    async salvar(codigo: string, payload: SalvarAlertaRequest): Promise<void> {
      await authService.authFetch<null>(`/api/notificacoes/alertas/${encodeURIComponent(codigo)}`, {
        method: 'PUT',
        body: JSON.stringify(payload)
      });
    },
    async disparar(codigo: string): Promise<void> {
      await authService.authFetch<null>(`/api/notificacoes/alertas/${encodeURIComponent(codigo)}/disparar-agora`, {
        method: 'POST'
      });
    }
  }
};
